"use client";

import React from "react";
import { alpha } from "@mui/material/styles";
import { PickerDay } from "@mui/x-date-pickers";
import moment from "moment-timezone";

const CustomDay = React.memo((props) => {
  const {
    day,
    selectedDay,
    availability,
    cutoffDate,
    meetingDuration,
    theme,
    userTimezone,
    checkDayAvailability,
    checkDaySelectable,
    hasMeetingDay,
    showAvailabilityDot = true,
    showMeetingDot = false,
    availabilityDotColor,
    meetingDotColor,
    todayBackgroundColor,
    todayTextColor,
    dayNumberFontSize,
    dayNumberFontWeight,
    softSelection = false,
    outsideCurrentMonth,
    ...other
  } = props;

  const dayStr = day.format("DD-MM-YYYY");

  // Availability is used for dot rendering; selectable state can be overridden.
  const hasAvailableSlot = React.useMemo(() => {
    return checkDayAvailability(day);
  }, [checkDayAvailability, day]);

  const canSelectDay = React.useMemo(() => {
    if (typeof checkDaySelectable === "function") {
      return checkDaySelectable(day);
    }
    return hasAvailableSlot;
  }, [checkDaySelectable, day, hasAvailableSlot]);

  const hasMeeting = React.useMemo(() => {
    if (typeof hasMeetingDay === "function") {
      return hasMeetingDay(day);
    }
    return false;
  }, [hasMeetingDay, day]);

  if (outsideCurrentMonth) {
    return (
      <PickerDay
        {...other}
        day={day}
        disabled={true}
        sx={{ visibility: "hidden" }}
      />
    );
  }

  const isPastDay = day.isBefore(cutoffDate, "day");
  const isToday = day.isSame(
    moment().tz(userTimezone || moment.tz.guess()),
    "day",
  );
  const isSelected = dayStr === selectedDay;

  const isDayAvailable = hasAvailableSlot && !isPastDay;
  const showAvailDot = isDayAvailable && showAvailabilityDot;
  const showMeetDot = !isPastDay && hasMeeting && showMeetingDot;
  const shouldSplitDots = showAvailDot && showMeetDot;
  const meetingDotResolvedColor = softSelection
    ? meetingDotColor || theme.palette.primary.main
    : isToday && theme.palette.mode === "dark"
    ? theme.palette.common.black
    : meetingDotColor || (isSelected ? "white" : theme.palette.primary.main);
  const availabilityDotResolvedColor = softSelection
    ? availabilityDotColor || theme.palette.secondary.main
    : isToday
    ? theme.palette.common.white
    : availabilityDotColor ||
      (isSelected ? "white" : theme.palette.primary.main);

  const bgColor = isSelected
    ? isToday
      ? todayBackgroundColor || theme.palette.primary.main
      : theme.palette.primary.main
    : isToday
    ? todayBackgroundColor || theme.palette.grey[400]
    : isDayAvailable
    ? theme.palette.tintedSurface
      ? theme.palette.background.paper
      : theme.palette.grey[100]
    : "transparent";

  const selectedBgColor = isToday
    ? todayBackgroundColor || theme.palette.primary.main
    : theme.palette.primary.main;

  const selectedFgColor = isToday
    ? todayTextColor || theme.palette.common.white
    : "white";

  const getDayStyles = () => {
    return {
      backgroundColor: bgColor,
      borderRadius: "50%",
      border: "none",
      outline: "none",
      opacity: !isDayAvailable ? 0.5 : 1,
      "&:focus": { outline: "none" },
      "&.Mui-selected": {
        backgroundColor: `${selectedBgColor} !important`,
        color: `${selectedFgColor} !important`,
      },
      color: isSelected
        ? "white"
        : isToday
        ? todayTextColor || theme.palette.common.white
        : "inherit",
      ...(dayNumberFontSize ? { fontSize: dayNumberFontSize } : {}),
      ...(dayNumberFontWeight ? { fontWeight: dayNumberFontWeight } : {}),
      ...(softSelection && {
        backgroundColor: isSelected
          ? alpha(
              theme.palette.primary.main,
              theme.palette.mode === "dark" ? 0.2 : 0.1,
            )
          : isToday
          ? "transparent"
          : bgColor,
        color: theme.palette.text.primary,
        border:
          isSelected && !isToday
            ? `1px solid ${theme.palette.primary.main}`
            : "1px solid transparent",
        opacity: isPastDay || !canSelectDay ? 0.5 : 1,
        "&:hover": {
          backgroundColor: `${alpha(
            theme.palette.primary.main,
            0.15,
          )} !important`,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        "&.Mui-selected": {
          backgroundColor: `${alpha(
            theme.palette.primary.main,
            theme.palette.mode === "dark" ? 0.2 : 0.1,
          )} !important`,
          color: `${theme.palette.text.primary} !important`,
          border: isToday
            ? "1px solid transparent !important"
            : `1px solid ${theme.palette.primary.main} !important`,
        },
      }),
    };
  };

  return (
    <div style={{ position: "relative" }}>
      <PickerDay
        {...other}
        day={day}
        disabled={isPastDay || !canSelectDay}
        data-today={isToday ? "true" : undefined}
        selected={isSelected}
        sx={getDayStyles()}
      />
      {showMeetDot && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            left: shouldSplitDots ? "42%" : "50%",
            transform: "translateX(-50%)",
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: meetingDotResolvedColor,
          }}
        />
      )}
      {showAvailDot && (
        <div
          style={{
            position: "absolute",
            bottom: 4,
            left: shouldSplitDots ? "58%" : "50%",
            transform: "translateX(-50%)",
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: availabilityDotResolvedColor,
          }}
        />
      )}
    </div>
  );
});

CustomDay.displayName = "CustomDay";

export default CustomDay;
